/**
 * Expandable Section Component
 * Collapsible content wrapper with smooth animations for the setup wizard
 */

import { useState, useId } from "react";
import { cn } from "@utils/cn";
import { ChevronDownIcon } from "./icons";

interface ExpandableSectionProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  variant?: "default" | "help" | "warning" | "info";
  className?: string;
}

const variantStyles = {
  default: {
    container: "border-gray-200 dark:border-gray-700",
    header:
      "bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800",
    headerText: "text-gray-900 dark:text-white",
    content: "bg-white dark:bg-gray-800",
  },
  help: {
    container: "border-blue-200 dark:border-blue-800",
    header:
      "bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30",
    headerText: "text-blue-900 dark:text-blue-100",
    content: "bg-blue-50/50 dark:bg-blue-900/10",
  },
  warning: {
    container: "border-amber-200 dark:border-amber-800",
    header:
      "bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30",
    headerText: "text-amber-900 dark:text-amber-100",
    content: "bg-amber-50/50 dark:bg-amber-900/10",
  },
  info: {
    container: "border-purple-200 dark:border-purple-800",
    header:
      "bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30",
    headerText: "text-purple-900 dark:text-purple-100",
    content: "bg-purple-50/50 dark:bg-purple-900/10",
  },
};

export function ExpandableSection({
  title,
  icon,
  children,
  defaultExpanded = false,
  variant = "default",
  className,
}: ExpandableSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const styles = variantStyles[variant];
  const contentId = useId();
  const headerId = useId();

  return (
    <div
      className={cn(
        "rounded-lg border overflow-hidden transition-all duration-200",
        styles.container,
        className,
      )}
    >
      <button
        id={headerId}
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between px-4 py-3 transition-colors",
          styles.header,
        )}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <div className="flex items-center gap-2">
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <span className={cn("font-medium text-sm", styles.headerText)}>
            {title}
          </span>
        </div>
        <ChevronDownIcon
          className={cn(
            "w-5 h-5 transition-transform duration-200",
            styles.headerText,
            isExpanded && "rotate-180",
          )}
        />
      </button>
      <div
        id={contentId}
        role="region"
        aria-labelledby={headerId}
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className={cn("px-4 py-3", styles.content)}>{children}</div>
        </div>
      </div>
    </div>
  );
}
